import { AfterViewInit, ChangeDetectorRef, Directive, inject, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';

import { CustomTableComponent } from '../../../../../shared/ui/custom-table/components/table/custom-table.component';
import { TableConfig } from '../../../../../shared/ui/custom-table/model/table-config.model';
import { TableColumn } from '../../../../../shared/ui/custom-table/model/table-column.model';
import { TableAction } from '../../../../../shared/ui/custom-table/model/table-action.model';
import { NotificationService } from '../../../../shared/services/notification.service';
import { ConfirmService } from '../../../../shared/services/confirm.service';
import { TierService } from '../../../../shared/services/tier.service';
import { ConfirmDialogDetail } from '../../../../shared/components/confirm-dialog.component';
import { mapTableState, tableStateEquals } from '../../../../shared/utils/table-state.utils';
import { TableStateSnapshot } from '../../../../shared/utils/table-types';

import { PaymentCardService } from '../../../../../modules/finanzas/paymentcard/paymentcard.service';
import { PaymentCardModel } from '../../../../../modules/finanzas/paymentcard/paymentcard.model';
import { PaymentCardFormDialogComponent } from '../payment-card-form-dialog/payment-card-form-dialog.component';
import { PaymentCardReadService } from '../../services/payment-card-read.service';
import {
  bankAccountLabel,
  expiryBadgeLabel,
  expiryBadgeVariant,
  maskCardNumber,
  PaymentCardRow,
  paymentCardExpiry,
} from '../../payment-card.utils';

@Directive()
export abstract class PaymentCardListBase implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('table') t1!: CustomTableComponent;
  @ViewChild('quickFilter') quickFilterTpl!: TemplateRef<any>;

  abstract readonly cardType: number;
  abstract readonly icon: string;
  abstract readonly title: string;
  abstract readonly subTitle: string;
  protected abstract readonly entityLabel: string;

  tableConfig!: TableConfig;
  quickSearch = '';

  protected readonly dialog = inject(MatDialog);
  protected readonly notify = inject(NotificationService);
  protected readonly confirm = inject(ConfirmService);
  protected readonly tier = inject(TierService);
  protected readonly paymentCardService = inject(PaymentCardService);
  private readonly reads = inject(PaymentCardReadService);
  private readonly cdr = inject(ChangeDetectorRef);

  protected readonly destroy$ = new Subject<void>();
  private readonly quickSearch$ = new Subject<string>();
  private rows: PaymentCardRow[] = [];
  private lastState: TableStateSnapshot | null = null;
  private loadSeq = 0;

  protected abstract rowActions(): TableAction[];
  protected abstract extraColumns(): TableColumn[];
  protected abstract deleteMessage(row: PaymentCardRow): string;

  protected readonly expandable: boolean = false;

  protected expandTemplate(): TemplateRef<any> | undefined {
    return undefined;
  }

  ngOnInit(): void {
    this.tableConfig = {
      columns: [
        { key: 'name', label: 'Nombre', sortable: true, sortKey: 'name', onCellClick: (row: PaymentCardRow) => this.edit(row) },
        { key: 'number', label: 'Número', sortable: true, sortKey: 'number', formatFn: (val: string) => maskCardNumber(val) },
        ...this.extraColumns(),
        { key: 'accountAssociated', label: 'Cuenta asociada', sortable: true, sortKey: 'accountAssociated', ellipsis: true, maxWidth: '320px' },
        {
          key: 'expiring', label: 'Caducidad', sortable: true, sortKey: 'expiring',
          badge: {
            variantFn: (_: string, row: PaymentCardRow) => expiryBadgeVariant(row.expiryStatus),
            labelFn: (value: string, row: PaymentCardRow) => expiryBadgeLabel(value, row.expiryStatus),
          },
        },
      ],
      actions: this.rowActions(),
      headerActions: [
        { label: '', icon: 'add', type: 'button', callback: () => this.create() },
      ],
      gridActions: [],
      filterView: '',
      defaultFilter: {},
      rowConfig: { clickable: false, expandable: this.expandable },
      selectable: false,
      pagination: true,
      noDataText: `No hay ${this.entityLabel}`,
    };
  }

  ngAfterViewInit(): void {
    this.tableConfig.quickFilterTemplate = this.quickFilterTpl;
    if (this.expandable) this.tableConfig.expandableRowTemplate = this.expandTemplate();

    this.quickSearch$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(value => this.t1.tableState.patchFilters({ search: value }));

    this.t1.tableState.state$
      .pipe(
        map(mapTableState),
        distinctUntilChanged(tableStateEquals),
        takeUntil(this.destroy$),
      )
      .subscribe(state => {
        const filters: any = state.filters || {};
        const search = String(filters.search || '');
        if (search !== this.quickSearch) this.quickSearch = search;
        this.lastState = state;
        this.load();
      });

    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearchChange(value: string): void {
    this.quickSearch = value || '';
    this.quickSearch$.next(this.quickSearch);
  }

  create(): void {
    if (!this.tier.requireAdvancedTier()) return;
    PaymentCardFormDialogComponent.open(this.dialog, { uid: null, paymentCardType: this.cardType })
      .subscribe(changed => { if (changed) this.load(); });
  }

  edit(row: PaymentCardRow): void {
    if (!this.tier.requireAdvancedTier()) return;
    PaymentCardFormDialogComponent.open(this.dialog, { uid: row.uid, paymentCardType: this.cardType, paymentCard: row })
      .subscribe(changed => { if (changed) this.load(); });
  }

  delete(row: PaymentCardRow): void {
    if (!this.tier.requireAdvancedTier()) return;
    this.confirm.open({
      title: 'Eliminar tarjeta',
      subtitle: row.name || undefined,
      message: this.deleteMessage(row),
      tone: 'danger',
      details: this.cardDetails(row),
      confirmText: 'Eliminar',
      confirmIcon: 'delete',
      loadingText: 'Eliminando tarjeta…',
      errorText: 'No se pudo eliminar la tarjeta',
      action: () => this.paymentCardService.deleteData(row.uid),
    }).subscribe(deleted => {
      if (!deleted) return;
      this.notify.ok('Tarjeta eliminada correctamente');
      this.load();
    });
  }

  protected cardDetails(row: PaymentCardRow): ConfirmDialogDetail[] {
    const details: ConfirmDialogDetail[] = [];
    if (row.number) details.push({ label: 'Número', value: maskCardNumber(row.number) });
    if (row.accountNumber) details.push({ label: 'Cuenta contable', value: row.accountNumber });
    if (row.accountAssociated) details.push({ label: 'Cuenta asociada', value: row.accountAssociated });
    return details;
  }

  protected load(): void {
    if (!this.t1 || !this.lastState) return;
    const state = this.lastState;
    const filters: any = state.filters || {};
    const seq = ++this.loadSeq;
    this.t1.tableState.setLoading(true);

    this.reads.list(this.cardType, {
      page: (state.pageIndex || 0) + 1,
      pageSize: state.pageSize || 10,
      search: String(filters.search || '').trim(),
      sortField: state.sortField || '',
      sortDirection: state.sortDirection || '',
    })
      .then(result => {
        if (seq !== this.loadSeq) return;
        const expanded = new Set(this.rows.filter(row => row.__expanded).map(row => row.uid));
        this.rows = result.items.map(card => this.toRow(card, expanded.has(card.uid)));
        this.t1.tableState.setData(this.rows);
        this.t1.tableState.setTotalItems(result.total);
        this.t1.tableState.setLoading(false);
        this.cdr.markForCheck();
      })
      .catch(error => {
        if (seq !== this.loadSeq) return;
        this.t1.tableState.setLoading(false);
        this.notify.error((error && error.error && error.error.message) || `No se pudieron cargar las ${this.entityLabel}`);
      });
  }

  private toRow(card: PaymentCardModel, expanded: boolean): PaymentCardRow {
    const expiry = paymentCardExpiry(card.monthExpiring, card.yearExpiring);
    return {
      ...card,
      accountAssociated: bankAccountLabel(card.bank, card.iban),
      expiring: expiry.label,
      expirySort: expiry.sort,
      expiryStatus: expiry.status,
      __expanded: expanded,
    };
  }
}
